import React, {
  memo,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  isValidElement,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import withDerivedProps from "lib/withDerivedProps";
import { createSystemEventMetadataMessage } from "Utils/AIUtils";
import {
  getIsAIEditDeleteButtonEnabled,
  getIsShowEditLearningMomentEnabled,
} from "Utils/ACLStoreUtils";

// DS Components
import {
  Button,
  IconButton,
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Tooltip,
} from "@toddle-edu/ds-web";
import { Popover, ArrowContainer } from "@toddle-edu/react-tiny-popover";
import {
  PlayFilled,
  PauseFilled,
  SettingsOutlined,
  VolumeHighOutlined,
  VolumeLowOutlined,
  TickLargeOutlined,
  RefreshArrowOutlined,
  DashboardFilled,
  DashboardOutlined,
  PencilLineOutlined,
  AddOutlined,
} from "@toddle-edu/ds-icons";

// Module
import { setToastMsg } from "Login/modules/LoginModule";

// UI Components
import Loading from "UIComponents/Loading";

// Local Components
import EditLearningMomentsModal from "./EditLearningMomentsModal";
import {
  formatTime,
  convertTimeToSeconds,
  secondsToTimestamp,
} from "./timestampUtils";

// scss
import classes from "./AITutorVideoModality.scss";

const styles = {
  popoverContainerStyle: {
    zIndex: 1100,
  },
};

const YOUTUBE_URL_REGEX = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|\?v=)([^#&?]*).*/;
const INITIAL_ACTIVE_POPOVER = { id: null, type: null };

const POPOVER_INFO = {
  BLOCKED: {
    title: "ai:label_is_locked",
    description: "ai:locked_checkpoint_description",
    icon: "🔒",
  },
  REACHED: {
    title: "ai:reached_label",
    description: "ai:reached_checkpoint_subtext",
    icon: "✅",
  },
  CLEARED: {
    title: "ai:label_cleared",
    description: "ai:cleared_checkpoint_description",
    icon: "🎉",
  },
  PLAY_BUTTON_BLOCKED: {
    title: "ai:clear_checkpoint",
    description: "ai:checkpoint_cleared_subtext",
    icon: "🚧",
  },
};

const getPopoverInfo = ({ checkpoint, type }) => {
  if (type === "INFO") {
    const { label, timestamp, learningMoment } = checkpoint || {};
    return {
      title: (
        <>
          <span>{label}</span>
          <span className="text-label-l">{` (${timestamp})`}</span>
        </>
      ),
      description: learningMoment,
      icon: "💡",
    };
  }

  return POPOVER_INFO[type] || {};
};

const CheckpointMarker = memo(props => {
  const {
    checkpoint,
    duration,
    activePopover,
    handleCheckpointClick,
    closePopover,
    onSendMessage,
    acknowledgedCheckpoints,
    maxWatchedTime,
    conversation,
    isViewMode,
    isAITutorChat,
    setIsPendingAttachmentUpdate,
    shouldBlockImReadyButton,
    isEditable,
    isSelected,
    onSelect,
    onDragStart,
    isDragging,
    dragPosition,
  } = props;

  const { t } = useI18n();

  const checkpointPercentage =
    duration > 0 ? (checkpoint.time / duration) * 100 : 0;

  const displayPercentage =
    isDragging && dragPosition != null ? dragPosition : checkpointPercentage;

  const checkpointMarkerRef = useRef(null);

  const handleCheckpointKeyDown = e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isEditable) {
        onSelect(checkpoint.id);
      } else {
        handleCheckpointClick(checkpoint.id);
      }
    }
  };

  const handleMarkerClick = useCallback(() => {
    if (isDragging) return;
    if (isEditable) {
      onSelect(checkpoint.id);
    } else {
      handleCheckpointClick(checkpoint.id);
    }
  }, [isDragging, isEditable, onSelect, checkpoint.id, handleCheckpointClick]);

  const handleMouseDown = useCallback(
    e => {
      if (!isEditable || !isSelected || !onDragStart || e.button !== 0) return;
      onDragStart(e, checkpoint);
    },
    [isEditable, isSelected, onDragStart, checkpoint]
  );

  const { id, type } = activePopover || {};

  const isActive = id === checkpoint.id;

  const popoverInfo = getPopoverInfo({ checkpoint, type });

  const onClickImReady = useCallback(async () => {
    closePopover();

    // create a divider like ------ Reached Checkpoint 1 ------
    const reachedCheckpointContent = t("ai:reached_label", {
      label: t("common:lowercase", { text: checkpoint.label }),
    });

    // Determine if this is first watch or rewatch
    const viewingState = _.includes(
      acknowledgedCheckpoints,
      checkpoint.timestamp
    )
      ? "REWATCH"
      : "FIRST_WATCH";

    // create user message for chat history to know user has reached this checkpoint
    const messagePrepend = createSystemEventMetadataMessage({
      eventType: "CHECKPOINT_REACHED",
      eventData: {
        video_context: {
          timestamp: checkpoint.timestamp,
        },
        completion_state: {
          viewing_state: viewingState,
          max_timestamp_seen: formatTime(maxWatchedTime),
          checkpoints_reached: _.uniq([
            ...acknowledgedCheckpoints,
            checkpoint.timestamp,
          ]),
        },
        checkpoint: {
          timestamp_label: checkpoint.timestamp,
          learning_moment: checkpoint.learningMoment,
          reflective_question: checkpoint.reflectiveQuestion,
        },
      },
    });

    setIsPendingAttachmentUpdate(true);

    const assistantMetadata = {
      videoContext: {
        timestamp: checkpoint.timestamp,
      },
    };

    await onSendMessage({
      content: reachedCheckpointContent,
      userMessageInput: [
        {
          authorType: "user",
          content: "",
          metadata: { messagePrepend },
        },
        {
          authorType: "divider",
          content: reachedCheckpointContent,
          metadata: { checkPointTime: checkpoint.timestamp },
        },
      ],
      aiConversationId: conversation?.id,
      assistantMetadata,
    });
    setIsPendingAttachmentUpdate(false);
  }, [
    checkpoint,
    closePopover,
    onSendMessage,
    t,
    acknowledgedCheckpoints,
    maxWatchedTime,
    conversation,
  ]);

  const isCheckpointBlocked = !_.includes(
    acknowledgedCheckpoints,
    checkpoint.timestamp
  );

  return (
    <>
      <div
        ref={checkpointMarkerRef}
        role="button"
        tabIndex={0}
        className={classNames(classes.checkpointMarker, {
          [classes.blocked]: isAITutorChat && isCheckpointBlocked,
          [classes.selected]: isSelected,
          [classes.dragging]: isDragging,
        })}
        style={{ left: `${displayPercentage}%` }}
        onClick={handleMarkerClick}
        onMouseDown={handleMouseDown}
        onKeyDown={handleCheckpointKeyDown}
        title={`${checkpoint.label}: ${formatTime(checkpoint.time)}`}
        aria-label={`${checkpoint.label} at ${formatTime(checkpoint.time)}`}
      />
      {isActive && !isViewMode && !isEditable ? (
        <Popover
          isOpen={true}
          padding={4}
          positions={["top", "left", "right"]}
          align={"center"}
          reposition={true}
          childrenRef={checkpointMarkerRef}
          isChildrenRefPassed={true}
          containerStyle={styles.popoverContainerStyle}
          content={({ position, childRect, popoverRect }) => {
            return (
              <ArrowContainer
                position={position}
                childRect={childRect}
                popoverRect={popoverRect}
                arrowColor={"var(--surface-primary-enabled)"}
                arrowSize={8}
              >
                <div className={classes.popoverContent}>
                  <div className={classes.popoverIconContainer}>
                    <div className={classes.popoverIcon}>
                      {popoverInfo.icon}
                    </div>
                  </div>
                  <div className={classes.popoverTextContent}>
                    <span className={classes.popoverTitle}>
                      <h6 style={{ display: "inline" }}>
                        {isValidElement(popoverInfo.title)
                          ? popoverInfo.title
                          : t(popoverInfo.title, { label: checkpoint.label })}
                      </h6>
                    </span>
                    <span
                      className={classNames(
                        "text-body",
                        classes.popoverDescription
                      )}
                    >
                      {t(popoverInfo.description)}
                    </span>

                    {type === "REACHED" ? (
                      <div className={classes.imReadyBtn}>
                        <Button
                          variant={"progressive"}
                          type={"fill"}
                          size={"medium"}
                          onClick={onClickImReady}
                          disabled={isViewMode || shouldBlockImReadyButton}
                        >
                          {t("ai:i_am_ready")}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </ArrowContainer>
            );
          }}
        />
      ) : null}
    </>
  );
});

// Extract YouTube video ID from URL
const getYoutubeVideoId = url => {
  const match = url?.match(YOUTUBE_URL_REGEX);
  if (match && match[2]?.length === 11) {
    return match[2];
  }
  return null;
};

// Convert time string (e.g., "0:30", "1:10", "1h2m3s", "5m30s", "45s") to seconds
const AITutorVideoModality = props => {
  const {
    url,
    checkpoints: propsCheckPoints,
    blockedAfterCheckpoint,
    acknowledgedCheckpoints = [],
    onSendMessage,
    isAITutorChat = false,
    isResizing = false,
    videoPlayerRef,
    conversation,
    mode = "edit",
    setIsPendingAttachmentUpdate = _.noop,
    shouldBlockImReadyButton = false,
    setToastMsg,
    attachmentId,
    updateAttachment,
    onAddCheckpointAtTime,
    onUpdateCheckpointTime,
    transcriptUrl,
    assessmentId,
    courseId,
    gradeId,
  } = props;

  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(50);
  const [isMuted, setIsMuted] = useState(false);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [activePopover, setActivePopover] = useState(INITIAL_ACTIVE_POPOVER);
  const [isBlocked, setIsBlocked] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(0);
  const [isHoveringBlockedArea, setIsHoveringBlockedArea] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [isPlayButtonPopoverOpen, setIsPlayButtonPopoverOpen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [rateOptions, setRateOptions] = useState([]);
  const [shouldRestrictSeeking, setShouldRestrictSeeking] = useState(
    isAITutorChat && !getIsAIEditDeleteButtonEnabled()
  );
  const [hasVideoError, setHasVideoError] = useState(false);
  // https://stackoverflow.com/a/62162401 - cropping to hide youtube default title, copy link, youtube logo and most importantly more videos recommendation overlay
  const [shouldApplyCropping, setShouldApplyCropping] = useState(false);
  const [hasVideoEnded, setHasVideoEnded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState(null);
  const [draggingCheckpointId, setDraggingCheckpointId] = useState(null);
  const [dragPosition, setDragPosition] = useState(null);

  const playerRef = useRef(null);
  const intervalRef = useRef(null);
  const lastCheckpointRef = useRef("");
  const containerRef = useRef(null);
  const currentTimeRef = useRef(0);
  const playButtonRef = useRef(null);
  const hasInitialSeekRef = useRef(false);
  const wasPlayingBeforeHiddenRef = useRef(false);
  const progressContainerRef = useRef(null);
  const dragPositionRef = useRef(null);
  const dragStartPosRef = useRef(null);

  const { t } = useI18n();

  const isViewMode = mode === "view";
  const videoId = getYoutubeVideoId(url);

  // Calculate start seconds based on acknowledged checkpoints
  const getStartSeconds = useCallback(() => {
    if (_.isEmpty(acknowledgedCheckpoints)) return 0;
    const lastCheckpointTimestamp = _.last(acknowledgedCheckpoints);
    if (!lastCheckpointTimestamp) return 0;
    return convertTimeToSeconds(lastCheckpointTimestamp);
  }, [acknowledgedCheckpoints]);

  const clampTimeToDuration = useCallback(
    timeInSeconds => {
      return Math.min(timeInSeconds, duration - 1);
    },
    [duration]
  );

  // Transform checkpoints to ensure time is in seconds
  const checkPoints = useMemo(
    () =>
      _.map(propsCheckPoints, (checkpoint, index) => {
        const labelKey = isAITutorChat
          ? "ai:checkpoint_with_label"
          : "ai:learning_moment_with_label";

        const checkpointTimestamp = _.get(checkpoint, "timestamp", checkpoint);
        const clampedSeconds = clampTimeToDuration(
          convertTimeToSeconds(checkpointTimestamp)
        );

        const timestamp = _.toString(secondsToTimestamp(clampedSeconds));

        return {
          timestamp, // original string - "3:23"
          time: clampedSeconds, // time in seconds - 203,
          id: `checkpoint-${index + 1}`,
          label: t(labelKey, { label: `${index + 1}` }),
          learningMoment: _.get(checkpoint, "learningMoment"),
          reflectiveQuestion: _.get(checkpoint, "reflectiveQuestion"),
        };
      }),
    [t, propsCheckPoints, isAITutorChat, clampTimeToDuration]
  );

  // Store timeouts for each checkpoint index
  const popoverTimeoutsRef = useRef({});

  useImperativeHandle(
    videoPlayerRef,
    () => ({
      getCurrentTimeStamp: () => formatTime(currentTimeRef.current),
      getCompletionState: () => {
        // Determine viewing state based on current position and acknowledged checkpoints
        const currentTimestamp = formatTime(currentTimeRef.current);
        const viewingState = _.includes(
          acknowledgedCheckpoints,
          currentTimestamp
        )
          ? "REWATCH"
          : "FIRST_WATCH";

        return {
          viewing_state: viewingState,
          max_timestamp_seen: formatTime(maxWatchedTime),
          checkpoints_reached: acknowledgedCheckpoints,
        };
      },
      showCheckpointPopoverByIndex: index => {
        if (_.isNumber(index) && index >= 0 && index < checkPoints.length) {
          const checkpoint = checkPoints[index];
          setActivePopover({ id: checkpoint.id, type: "INFO" });

          // Clear previous timeout for this index
          if (popoverTimeoutsRef.current[index]) {
            clearTimeout(popoverTimeoutsRef.current[index]);
          }

          // Set new timeout for this index
          popoverTimeoutsRef.current[index] = setTimeout(() => {
            // Only hide if the same popover is still active
            setActivePopover(prev =>
              prev.id === checkpoint.id && prev.type === "INFO"
                ? INITIAL_ACTIVE_POPOVER
                : prev
            );
            popoverTimeoutsRef.current[index] = null;
          }, 5000);
        }
      },
      resetVideo: () => {
        if (!player) return;

        // Seek to start
        player.seekTo(0, true);
        player.pauseVideo();

        // Reset state
        currentTimeRef.current = 0;
        setCurrentTime(0);
        setMaxWatchedTime(0);
        setIsBlocked(false);
        setActivePopover(INITIAL_ACTIVE_POPOVER);
        lastCheckpointRef.current = "";
        setHasVideoEnded(false);
      },
    }),
    [maxWatchedTime, acknowledgedCheckpoints, checkPoints, player]
  );

  // Throttled state update for smooth progress bar
  const throttledSetCurrentTime = useMemo(
    () =>
      _.throttle(time => {
        setCurrentTime(time);
      }, 100),
    []
  );

  // Load YouTube IFrame API
  useEffect(() => {
    if (window.YT) {
      setIsAPIReady(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    window.onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
    };
  }, []);

  // Initialize YouTube player
  useEffect(() => {
    if (!isAPIReady || !playerRef.current || player) return;

    const ytPlayer = new window.YT.Player(playerRef.current, {
      height: "100%",
      width: "100%",
      videoId,
      playerVars: {
        controls: 0, // hides YT default controls
        modestbranding: 1, // no YT logo
        rel: 0, // no related videos at end
        showinfo: 0, // no video info before video starts
        iv_load_policy: 3, // Hide annotations,
        playsinline: 1, // play video inline on iOS
        cc_load_policy: 1, // show closed captions
        cc_lang_pref: "en", // default caption language
        fs: 0, // disable fullscreen button
      },
      events: {
        onReady: event => {
          const yt = event.target;
          setPlayer(yt);
          setDuration(yt.getDuration());
          setVolume(yt.getVolume());
          setIsPlayerReady(true);
          setHasVideoError(false);
          setRateOptions(
            _.map(yt.getAvailablePlaybackRates(), rate => ({
              key: `${rate}`,
              value: `${rate}`,
              label: `${rate}x`,
            }))
          );
        },
        onStateChange: event => {
          const { data, target } = event;
          setIsPlaying(data === window.YT.PlayerState.PLAYING);

          if (
            data === window.YT.PlayerState.CUED ||
            data === window.YT.PlayerState.PLAYING
          ) {
            setDuration(target.getDuration());
          }

          // if (
          //   data === window.YT.PlayerState.UNSTARTED ||
          //   data === window.YT.PlayerState.CUED
          // ) {
          //   setShouldApplyCropping(false);
          // } else {
          //   setShouldApplyCropping(true);
          // }
        },
        onError: () => {
          // Just remove loading state on error
          setIsPlayerReady(true);
          setHasVideoError(true);
        },
      },
    });
  }, [isAPIReady, playerRef, player, videoId]);

  // Reload video when URL changes
  useEffect(() => {
    if (!player || !videoId) return;

    setIsPlayerReady(false);
    setHasVideoError(false);
    setHasVideoEnded(false);

    const startSeconds = getStartSeconds();
    player.cueVideoById({
      videoId,
      startSeconds,
    });

    setCurrentTime(startSeconds);
    currentTimeRef.current = startSeconds;

    // Only set maxWatchedTime if we have a valid start time from checkpoints
    if (startSeconds > 0) {
      setMaxWatchedTime(startSeconds);
    } else {
      setMaxWatchedTime(0);
      hasInitialSeekRef.current = false;
    }

    lastCheckpointRef.current = "";
    setIsBlocked(false);

    // Set player ready again after new video loads
    const checkReady = setInterval(() => {
      if (player.getDuration() > 0) {
        setIsPlayerReady(true);
        clearInterval(checkReady);
      }
    }, 100);

    return () => clearInterval(checkReady);
  }, [videoId, player]);

  // Seek to last acknowledged checkpoint on mount
  useEffect(() => {
    if (
      !player ||
      !isPlayerReady ||
      hasInitialSeekRef.current ||
      _.isEmpty(acknowledgedCheckpoints)
    ) {
      return;
    }

    const startSeconds = getStartSeconds();
    if (startSeconds > 0) {
      player.seekTo(startSeconds, true);
      currentTimeRef.current = startSeconds;
      setCurrentTime(startSeconds);
      setMaxWatchedTime(startSeconds);

      hasInitialSeekRef.current = true;
    }
  }, [player, isPlayerReady, acknowledgedCheckpoints, getStartSeconds]);

  // Pause video when not visible (e.g., modal overlay, tab switch, or navigation)
  useEffect(() => {
    if (!containerRef.current || !player) return;

    const checkVisibility = () => {
      if (!containerRef.current) return true;

      const rect = containerRef.current?.getBoundingClientRect?.();
      if (!rect) return true;

      // Check if element is in viewport
      const isInViewport = rect.top >= 0 && rect.bottom <= window.innerHeight;

      // Check if element is covered by another element
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const elementAtPoint = document.elementFromPoint?.(centerX, centerY);

      // Element is visible if it or one of its children is at the center point
      const isElementVisible =
        elementAtPoint &&
        (containerRef.current?.contains?.(elementAtPoint) ||
          elementAtPoint === containerRef.current);

      return isInViewport && isElementVisible;
    };

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden && checkVisibility();

      if (!isVisible && isPlaying) {
        wasPlayingBeforeHiddenRef.current = true;
        player.pauseVideo();
      } else if (isVisible && wasPlayingBeforeHiddenRef.current && !isBlocked) {
        wasPlayingBeforeHiddenRef.current = false;
        player.playVideo();
      }
    };

    // Listen for document visibility changes (tab switching)
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Intersection Observer for scrolling out of view
    const intersectionObserver = new IntersectionObserver(
      entries => {
        const [entry] = entries;

        if (!entry.isIntersecting && isPlaying) {
          wasPlayingBeforeHiddenRef.current = true;
          player.pauseVideo();
        } else if (
          entry.isIntersecting &&
          wasPlayingBeforeHiddenRef.current &&
          !isBlocked &&
          !document.hidden
        ) {
          // Double check it's actually visible (not covered by modal)
          const isActuallyVisible = checkVisibility();
          if (isActuallyVisible) {
            wasPlayingBeforeHiddenRef.current = false;
            player.playVideo();
          }
        }
      },
      { threshold: 0.1 }
    );

    // ResizeObserver to detect size changes that might indicate overlay coverage
    const resizeObserver = new ResizeObserver(() => {
      if (isPlaying) {
        const isVisible = checkVisibility();
        if (!isVisible) {
          wasPlayingBeforeHiddenRef.current = true;
          player.pauseVideo();
        }
      }
    });

    // MutationObserver to detect DOM changes that might cover the video
    const mutationObserver = new MutationObserver(() => {
      if (isPlaying) {
        const isVisible = checkVisibility();
        if (!isVisible) {
          wasPlayingBeforeHiddenRef.current = true;
          player.pauseVideo();
        }
      }
    });

    intersectionObserver.observe(containerRef.current);
    resizeObserver.observe(containerRef.current);

    // Observe the document body for modal/overlay additions
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      intersectionObserver.disconnect();
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [player, isPlaying, isBlocked]);

  // Check if user should be blocked at a checkpoint
  const checkBlockedCheckpoint = useCallback(
    time => {
      if (!isAITutorChat || _.isNil(blockedAfterCheckpoint)) return false;

      const blockedTime = convertTimeToSeconds(blockedAfterCheckpoint);

      return time > blockedTime;
    },
    [isAITutorChat, blockedAfterCheckpoint]
  );

  // Update video ended state based on time
  const updateVideoEndedState = useCallback(
    time => {
      const videoEnded = duration > 0 && time >= duration - 1;
      setHasVideoEnded(prevEnded => {
        if (prevEnded !== videoEnded) {
          return videoEnded;
        }
        return prevEnded;
      });
    },
    [duration]
  );

  // Time tracking and checkpoint detection
  useEffect(() => {
    if (isResizing) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (!player || !isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      const time = player.getCurrentTime();
      currentTimeRef.current = time;

      // Update UI with throttled function for smooth progress bar
      throttledSetCurrentTime(time);

      // Check if video has ended (within 1 second of duration)
      updateVideoEndedState(time);

      // Update max watched time if current time exceeds it
      if (isAITutorChat) {
        setMaxWatchedTime(prevMax => Math.max(prevMax, time));
      }

      // Block playback if trying to play beyond blocked checkpoint

      if (checkBlockedCheckpoint(time)) {
        setIsBlocked(true);
        player.pauseVideo();

        // Seek back to the blocked checkpoint position
        const blockedTime = convertTimeToSeconds(blockedAfterCheckpoint);
        const blockedCheckpoint = _.find(
          checkPoints,
          cp => cp.time === blockedTime
        );

        player.seekTo(blockedTime, true);
        currentTimeRef.current = blockedTime;
        setCurrentTime(blockedTime);

        if (
          blockedCheckpoint &&
          !_.includes(acknowledgedCheckpoints, blockedAfterCheckpoint)
        ) {
          setActivePopover({ id: blockedCheckpoint.id, type: "REACHED" });
        }
        return;
      }

      // Detect and handle checkpoint reached (check only every ~500ms)
      if (isAITutorChat && Math.floor(time * 2) % 1 === 0) {
        const currentCheckpoint = _.find(
          checkPoints,
          checkpoint =>
            time >= checkpoint.time &&
            time < checkpoint.time + 1 && // + 1 is CHECKPOINT_THRESHOLD
            lastCheckpointRef.current !== checkpoint.id
        );

        if (
          currentCheckpoint &&
          currentCheckpoint.timestamp === blockedAfterCheckpoint
        ) {
          lastCheckpointRef.current = currentCheckpoint.id;
          player.pauseVideo();
        }
      }
    }, 100); // Update every 100ms for smooth progress bar

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    player,
    isPlaying,
    isResizing,
    checkPoints,
    checkBlockedCheckpoint,
    blockedAfterCheckpoint,
    throttledSetCurrentTime,
    acknowledgedCheckpoints,
    isAITutorChat,
    updateVideoEndedState,
  ]);

  // Automatically unblock when user seeks/plays before blocked checkpoint
  useEffect(() => {
    if (isBlocked) {
      if (!_.isNil(blockedAfterCheckpoint)) {
        const blockedTime = convertTimeToSeconds(blockedAfterCheckpoint);
        if (currentTime < blockedTime) {
          setIsBlocked(false);
        }
      } else {
        setIsBlocked(false);
      }
    }
  }, [isBlocked, currentTime, blockedAfterCheckpoint]);

  const togglePlayPause = useCallback(() => {
    if (!player) return;

    // Allow playing if not blocked OR if current time is before blocked checkpoint
    if (isBlocked && !_.isNil(blockedAfterCheckpoint)) {
      const blockedTime = convertTimeToSeconds(blockedAfterCheckpoint);
      if (currentTime <= blockedTime) {
        setIsBlocked(false);
      }
    }

    if (isPlaying) {
      player.pauseVideo();
    } else {
      player.playVideo();
    }
  }, [player, isPlaying, isBlocked, currentTime, blockedAfterCheckpoint]);

  const handleRestartVideo = useCallback(() => {
    if (!player) return;

    // Reset to start
    player.seekTo(0, true);
    setCurrentTime(0);
    currentTimeRef.current = 0;
    setHasVideoEnded(false);

    // Start playing
    player.playVideo();
  }, [player]);

  const handleSeek = useCallback(
    percentage => {
      if (!player || duration <= 0) return;

      let newTime = (percentage / 100) * duration;
      if (shouldRestrictSeeking) {
        // Restrict seeking to only the watched portion
        newTime = Math.min(newTime, maxWatchedTime);
      }

      // Prevent seeking past the blocked checkpoint
      if (checkBlockedCheckpoint(newTime)) {
        const blockedTime = convertTimeToSeconds(blockedAfterCheckpoint);
        const blockedCheckpoint = _.find(
          checkPoints,
          cp => cp.time === blockedTime
        );

        if (blockedCheckpoint) {
          setActivePopover({ id: blockedCheckpoint.id, type: "BLOCKED" });
        }

        return;
      }

      // Clear blocked state if seeking to allowed area
      if (isBlocked) {
        setIsBlocked(false);
      }

      // Update ref immediately to prevent race condition with interval
      currentTimeRef.current = newTime;
      setCurrentTime(newTime);
      player.seekTo(newTime, true);
      setActivePopover(INITIAL_ACTIVE_POPOVER);

      // Check if seeked to end of video
      updateVideoEndedState(newTime);
    },
    [
      player,
      duration,
      checkBlockedCheckpoint,
      checkPoints,
      blockedAfterCheckpoint,
      isBlocked,
      shouldRestrictSeeking,
      maxWatchedTime,
      updateVideoEndedState,
    ]
  );

  const handleVolumeChange = useCallback(
    newVolume => {
      if (!player) return;

      player.setVolume(newVolume);
      setVolume(newVolume);

      if (newVolume === 0) {
        setIsMuted(true);
      } else {
        setIsMuted(false);
        if (isMuted) {
          player.unMute();
        }
      }
    },
    [player, isMuted]
  );

  const toggleMute = useCallback(() => {
    if (!player) return;

    if (isMuted) {
      player.unMute();
      player.setVolume(30);
      setVolume(30);
      setIsMuted(false);
    } else {
      player.mute();
      player.setVolume(0);
      setVolume(0);
      setIsMuted(true);
    }
  }, [player, isMuted]);

  const onChangeSettings = ({ key }) => {
    if (!player) return;

    if (key === "RESTRICT_SEEKING") {
      setShouldRestrictSeeking(prev => !prev);
    }
  };

  const onChangePlaybackRate = ({ key }) => {
    if (!player) return;

    const rate = parseFloat(key);
    player.setPlaybackRate(rate);
    setPlaybackRate(rate);
  };

  const onChangeCaptionSetting = () => {
    if (!player) return;

    const newCaptionsState = !showCaptions;
    setShowCaptions(newCaptionsState);

    // Toggle captions/subtitles
    if (newCaptionsState) {
      player.loadModule("captions");

      setToastMsg({
        type: "success",
        msg: "toastMsgs:captions_enabled",
      });
    } else {
      player.unloadModule("captions");

      setToastMsg({
        type: "success",
        msg: "toastMsgs:captions_disabled",
      });
    }
  };

  const settingsOptions = useMemo(() => {
    const options = [
      {
        key: "RESTRICT_SEEKING",
        label: "Restrict seeking",
        suffix: shouldRestrictSeeking ? <TickLargeOutlined /> : null,
      },
    ];

    return options;
  }, [shouldRestrictSeeking]);

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  const closePopover = useCallback(() => {
    setActivePopover(INITIAL_ACTIVE_POPOVER);
  }, []);

  const handleCheckpointClick = useCallback(
    checkpointId => {
      if (!isAITutorChat || isViewMode) return;

      const checkpoint = _.find(checkPoints, { id: checkpointId });
      if (checkpoint && !checkBlockedCheckpoint(checkpoint.time)) {
        handleSeek((checkpoint.time / duration) * 100);
      }
    },
    [
      isAITutorChat,
      checkPoints,
      checkBlockedCheckpoint,
      handleSeek,
      duration,
      isViewMode,
    ]
  );

  const handleProgressHover = useCallback(
    e => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const time = (percentage / 100) * duration;
      setHoverTime(time);
      setHoverPosition(percentage);

      // Check if hovering over blocked area
      const isCheckpointBlocked = checkBlockedCheckpoint(time);

      // Check if hovering beyond max watched time (seeking restriction)
      const isSeekingRestricted =
        shouldRestrictSeeking && time > maxWatchedTime;

      const isHoveringBlocked = isCheckpointBlocked || isSeekingRestricted;
      setIsHoveringBlockedArea(isHoveringBlocked);

      // Check if hovering over a checkpoint
      const HOVER_THRESHOLD = 2; // seconds
      const hoveredCheckpoint = _.find(
        checkPoints,
        checkpoint => Math.abs(checkpoint.time - time) <= HOVER_THRESHOLD
      );

      if (isAITutorChat) {
        if (activePopover?.type === "REACHED") return;

        if (hoveredCheckpoint) {
          if (isCheckpointBlocked) {
            setActivePopover({ id: hoveredCheckpoint.id, type: "BLOCKED" });
          } else if (blockedAfterCheckpoint !== hoveredCheckpoint?.timestamp) {
            setActivePopover({ id: hoveredCheckpoint.id, type: "CLEARED" });
          }
        } else {
          setActivePopover(INITIAL_ACTIVE_POPOVER);
        }
      } else {
        if (hoveredCheckpoint) {
          setActivePopover({ id: hoveredCheckpoint.id, type: "INFO" });
        } else {
          setActivePopover(INITIAL_ACTIVE_POPOVER);
        }
      }
    },
    [
      duration,
      checkBlockedCheckpoint,
      activePopover,
      checkPoints,
      isAITutorChat,
      blockedAfterCheckpoint,
      shouldRestrictSeeking,
      maxWatchedTime,
    ]
  );

  const handleProgressMouseLeave = useCallback(() => {
    setHoverTime(null);
    setIsHoveringBlockedArea(false);

    if (activePopover?.type !== "REACHED") {
      setActivePopover(INITIAL_ACTIVE_POPOVER);
    }
  }, [activePopover]);

  const isEditable = !!onAddCheckpointAtTime;

  const handleSelectCheckpoint = useCallback(checkpointId => {
    setSelectedCheckpointId(prev =>
      prev === checkpointId ? null : checkpointId
    );
  }, []);

  const handleCheckpointDragStart = useCallback(
    (e, checkpoint) => {
      if (!onUpdateCheckpointTime) return;
      dragStartPosRef.current = {
        x: e.clientX,
        checkpointId: checkpoint.id,
        initialPct: duration > 0 ? (checkpoint.time / duration) * 100 : 0,
        started: false,
      };

      const onMouseMove = moveEvent => {
        if (!dragStartPosRef.current || !progressContainerRef.current) return;
        const dx = Math.abs(moveEvent.clientX - dragStartPosRef.current.x);
        if (dx > 3 && !dragStartPosRef.current.started) {
          dragStartPosRef.current.started = true;
          setDraggingCheckpointId(dragStartPosRef.current.checkpointId);
        }
        if (dragStartPosRef.current.started) {
          const rect = progressContainerRef.current.getBoundingClientRect();
          const xPos = Math.max(
            0,
            Math.min(moveEvent.clientX - rect.left, rect.width)
          );
          const pct = (xPos / rect.width) * 100;
          dragPositionRef.current = pct;
          setDragPosition(pct);
        }
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);

        // Commit drag position if drag actually started
        if (dragStartPosRef.current?.started) {
          const finalPosition = dragPositionRef.current;
          if (finalPosition != null) {
            const cpId = dragStartPosRef.current.checkpointId;
            const checkpointIndex = _.findIndex(
              checkPoints,
              cp => cp.id === cpId
            );
            if (checkpointIndex !== -1 && duration > 0) {
              const newTimeSeconds = (finalPosition / 100) * duration;
              const clamped = Math.max(0, Math.min(newTimeSeconds, duration));
              const mins = Math.floor(clamped / 60);
              const secs = Math.floor(clamped % 60);
              const timestamp = `${mins}:${String(secs).padStart(2, "0")}`;
              onUpdateCheckpointTime(checkpointIndex, timestamp);
            }
          }
          setDraggingCheckpointId(null);
          setDragPosition(null);
          dragPositionRef.current = null;
        }

        dragStartPosRef.current = null;
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [onUpdateCheckpointTime, duration, checkPoints]
  );

  // Deselect when clicking outside the progress bar
  const handleProgressContainerClick = useCallback(
    e => {
      // If clicking directly on the progress container (not on a marker), deselect
      if (isEditable && selectedCheckpointId && e.target === e.currentTarget) {
        setSelectedCheckpointId(null);
      }
    },
    [isEditable, selectedCheckpointId]
  );

  const getVolumeIcon = useCallback(() => {
    if (isMuted) {
      return (
        <div className={classes.volumeIconWrapper}>
          <VolumeLowOutlined variant={"on"} />
          <div className={classes.mutedLine} />
        </div>
      );
    }

    if (volume > 50) {
      return <VolumeHighOutlined variant={"on"} />;
    }

    return <VolumeLowOutlined variant={"on"} />;
  }, [isMuted, volume]);

  const handlePlayButtonMouseEnter = useCallback(() => {
    if (isBlocked) {
      setIsPlayButtonPopoverOpen(true);
    }
  }, [isBlocked]);

  const handlePlayButtonMouseLeave = useCallback(() => {
    setIsPlayButtonPopoverOpen(false);
  }, []);

  const handlePlayButtonClick = useCallback(() => {
    if (isBlocked) {
      return;
    }

    if (hasVideoEnded) {
      handleRestartVideo();
    } else {
      togglePlayPause();
    }
  }, [hasVideoEnded, isBlocked, handleRestartVideo, togglePlayPause]);

  const playButtonStyle = useMemo(() => {
    if (isBlocked) return { color: "var(--icon-disabled)" };
    return {};
  }, [isBlocked]);

  const playButtonIcon = useMemo(() => {
    if (hasVideoEnded) {
      return <RefreshArrowOutlined variant={"on"} />;
    }
    if (isPlaying) {
      return <PauseFilled variant={"on"} />;
    }
    return <PlayFilled variant={"on"} style={playButtonStyle} />;
  }, [hasVideoEnded, isPlaying, playButtonStyle]);

  const showEditButton =
    !isViewMode &&
    !isAITutorChat &&
    !_.isEmpty(checkPoints) &&
    !onAddCheckpointAtTime &&
    getIsShowEditLearningMomentEnabled();

  return (
    <div
      className={classNames(classes.container, {
        [classes.isResizing]: isResizing,
      })}
      ref={containerRef}
    >
      {/* Video Container */}
      <div
        className={classNames(classes.videoContainer, {
          [classes.videoContainerInset]: showEditButton,
        })}
      >
        <div
          className={classNames(classes.croppingDiv, {
            [classes.applyCropping]: shouldApplyCropping,
          })}
        >
          <div
            className={classNames(classes.divToCrop, {
              [classes.applyCropping]: shouldApplyCropping,
            })}
          >
            <div className={classes.playerWrapper}>
              <div ref={playerRef} className={classes.player} />
            </div>
          </div>
        </div>
        {isBlocked ? <div className={classes.blockedOverlay} /> : null}
        {hasVideoEnded ? (
          <div
            className={classes.endOverlay}
            aria-hidden="true"
            role="presentation"
          />
        ) : null}
      </div>

      {!isAPIReady || !isPlayerReady ? (
        <div className={classes.loadingOverlay}>
          <Loading position="center" />
        </div>
      ) : null}

      {/* Controls */}
      {!hasVideoError ? (
        <div className={classes.controls}>
          {/* Edit Learning Moments Button */}
          {showEditButton && (
            <div className={classes.editLearningMomentsButton}>
              <Button
                dsVersion="2.0"
                variant="neutral"
                type="outlined"
                size="small"
                icon={<PencilLineOutlined />}
                onClick={() => setIsEditModalOpen(true)}
              >
                {t("ai:edit_learning_moments")}
              </Button>
            </div>
          )}
          {/* Progress Bar */}
          <div
            ref={progressContainerRef}
            className={classNames(classes.progressContainer, {
              [classes.blockedSeekbar]: isHoveringBlockedArea,
              [classes.hasAddCheckpoint]: !!onAddCheckpointAtTime,
            })}
            onMouseMove={handleProgressHover}
            onMouseLeave={handleProgressMouseLeave}
            onClick={handleProgressContainerClick}
          >
            {onAddCheckpointAtTime &&
              !_.isNil(hoverTime) &&
              !draggingCheckpointId &&
              !selectedCheckpointId && (
                <div
                  className={classes.addCheckpointLine}
                  style={{ left: `${hoverPosition}%` }}
                >
                  <button
                    className={classes.addCheckpointButton}
                    onClick={e => {
                      e.stopPropagation();
                      onAddCheckpointAtTime(formatTime(hoverTime));
                    }}
                    title={t("ai:add_checkpoint")}
                    aria-label={t("ai:add_checkpoint")}
                  >
                    <AddOutlined size="xxx-small" />
                  </button>
                </div>
              )}
            <div className={classes.progressWrapper}>
              <input
                type="range"
                min="0"
                max="100"
                value={progressPercentage}
                onChange={e => handleSeek(parseFloat(e.target.value))}
                className={classNames(classes.progressBar, {
                  [classes.dragActive]: !!draggingCheckpointId,
                })}
              />
              <div
                className={classes.progressFill}
                style={{ width: `${progressPercentage}%` }}
              />
              {!_.isNil(hoverTime) &&
                !activePopover?.id &&
                !draggingCheckpointId && (
                  <div
                    className={classes.timeTooltip}
                    style={{ left: `${hoverPosition}%` }}
                  >
                    {formatTime(hoverTime)}
                  </div>
                )}
              {draggingCheckpointId && dragPosition != null && (
                <div
                  className={classes.timeTooltip}
                  style={{ left: `${dragPosition}%` }}
                >
                  {formatTime((dragPosition / 100) * duration)}
                </div>
              )}

              {/* Checkpoints */}
              <div className={classes.checkpointsContainer}>
                {_.map(checkPoints, checkpoint => (
                  <CheckpointMarker
                    key={checkpoint.id}
                    checkpoint={checkpoint}
                    duration={duration}
                    activePopover={activePopover}
                    handleCheckpointClick={handleCheckpointClick}
                    closePopover={closePopover}
                    onSendMessage={onSendMessage}
                    isAITutorChat={isAITutorChat}
                    acknowledgedCheckpoints={acknowledgedCheckpoints}
                    maxWatchedTime={maxWatchedTime}
                    conversation={conversation}
                    isViewMode={isViewMode}
                    setIsPendingAttachmentUpdate={setIsPendingAttachmentUpdate}
                    shouldBlockImReadyButton={shouldBlockImReadyButton}
                    isEditable={isEditable}
                    isSelected={selectedCheckpointId === checkpoint.id}
                    onSelect={handleSelectCheckpoint}
                    onDragStart={handleCheckpointDragStart}
                    isDragging={draggingCheckpointId === checkpoint.id}
                    dragPosition={
                      draggingCheckpointId === checkpoint.id
                        ? dragPosition
                        : null
                    }
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Control Row */}
          <div className={classes.controlRow}>
            <div className={classes.leftControls}>
              <div
                ref={playButtonRef}
                onMouseEnter={handlePlayButtonMouseEnter}
                onMouseLeave={handlePlayButtonMouseLeave}
                className={classNames({
                  [classes.blockedPlayButton]: isBlocked,
                })}
              >
                <IconButton
                  icon={playButtonIcon}
                  size="medium"
                  variant="neutral"
                  onClick={handlePlayButtonClick}
                  disabled={!player}
                />
              </div>

              {isBlocked &&
              isPlayButtonPopoverOpen &&
              !isViewMode &&
              _.isEmpty(activePopover?.type) ? (
                <Popover
                  isOpen={true}
                  padding={4}
                  positions={["top", "right"]}
                  align={"center"}
                  reposition={true}
                  childrenRef={playButtonRef}
                  isChildrenRefPassed={true}
                  containerStyle={styles.popoverContainerStyle}
                  content={({ position, childRect, popoverRect }) => {
                    const popoverInfo = getPopoverInfo({
                      type: "PLAY_BUTTON_BLOCKED",
                    });

                    return (
                      <ArrowContainer
                        position={position}
                        childRect={childRect}
                        popoverRect={popoverRect}
                        arrowColor={"var(--surface-primary-enabled)"}
                        arrowSize={8}
                      >
                        <div className={classes.popoverContent}>
                          <div className={classes.popoverIconContainer}>
                            <div className={classes.popoverIcon}>
                              {popoverInfo.icon}
                            </div>
                          </div>
                          <div className={classes.popoverTextContent}>
                            <span className={classes.popoverTitle}>
                              <h6 style={{ display: "inline" }}>
                                {t(popoverInfo.title)}
                              </h6>
                            </span>
                            <span
                              className={classNames(
                                "text-body",
                                classes.popoverDescription
                              )}
                            >
                              {t(popoverInfo.description)}
                            </span>
                          </div>
                        </div>
                      </ArrowContainer>
                    );
                  }}
                />
              ) : null}

              <div className={classes.volumeControl}>
                <IconButton
                  icon={getVolumeIcon()}
                  variant="neutral"
                  onClick={toggleMute}
                />

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={e => handleVolumeChange(parseFloat(e.target.value))}
                  className={classes.volumeSlider}
                  style={{
                    background: `linear-gradient(to right, var(--surface-primary-enabled) 0%, var(--surface-primary-enabled) ${volume}%, rgba(255, 255, 255, 0.3) ${volume}%, rgba(255, 255, 255, 0.3) 100%)`,
                  }}
                />
              </div>

              <span className={classNames("text-label", classes.timeDisplay)}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className={classes.rightControls}>
              {/* playback speed dropdown */}
              {!_.isEmpty(rateOptions) ? (
                <Dropdown
                  overlay={
                    <DropdownMenu
                      dsVersion="2.0"
                      key={`${playbackRate}`}
                      options={rateOptions}
                      onClick={onChangePlaybackRate}
                      value={`${playbackRate}`}
                      showSelection={true}
                      selectionType="tick"
                    />
                  }
                >
                  <ButtonDisclosure
                    variant="neutral"
                    type="fill"
                    size="medium"
                    className={classes.playbackRateButton}
                  >
                    {`${playbackRate}x`}
                  </ButtonDisclosure>
                </Dropdown>
              ) : null}

              {/* caption setting icon */}
              <Tooltip tooltip={t("ai:captions")}>
                <IconButton
                  icon={
                    showCaptions ? (
                      <DashboardFilled variant="on" />
                    ) : (
                      <DashboardOutlined variant="on" />
                    )
                  }
                  size="medium"
                  variant="neutral"
                  onClick={onChangeCaptionSetting}
                />
              </Tooltip>

              {/* video seeking setting for internal user */}
              {isAITutorChat && getIsAIEditDeleteButtonEnabled() ? (
                <Dropdown
                  overlay={
                    <DropdownMenu
                      options={settingsOptions}
                      onClick={onChangeSettings}
                      overlayHeight={"fit-content"}
                    />
                  }
                >
                  <IconButton
                    icon={<SettingsOutlined variant={"on"} />}
                    size="medium"
                    variant="neutral"
                    disabled={_.isEmpty(settingsOptions)}
                  />
                </Dropdown>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* Edit Learning Moments Modal */}
      {isEditModalOpen && (
        <EditLearningMomentsModal
          checkpoints={propsCheckPoints}
          url={url}
          attachmentId={attachmentId}
          updateAttachment={updateAttachment}
          setIsPendingAttachmentUpdate={setIsPendingAttachmentUpdate}
          setToastMsg={setToastMsg}
          onClose={() => setIsEditModalOpen(false)}
          videoDuration={duration}
          transcriptUrl={transcriptUrl}
          conversation={conversation}
          assessmentId={assessmentId}
          courseId={courseId}
          gradeId={gradeId}
          renderVideoPlayer={({
            checkpoints: cps,
            onAddCheckpointAtTime,
            onUpdateCheckpointTime,
          }) => (
            <AITutorVideoModality
              url={url}
              checkpoints={cps}
              mode="view"
              onAddCheckpointAtTime={onAddCheckpointAtTime}
              onUpdateCheckpointTime={onUpdateCheckpointTime}
            />
          )}
        />
      )}
    </div>
  );
};

AITutorVideoModality.propTypes = {
  url: PropTypes.string.isRequired,
  checkpoints: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        timestamp: PropTypes.string.isRequired,
        learningMoment: PropTypes.string,
        reflectiveQuestion: PropTypes.string,
      }),
    ])
  ),
  blockedAfterCheckpoint: PropTypes.string,
  mode: PropTypes.oneOf(["view", "edit"]),
  attachmentId: PropTypes.string,
  updateAttachment: PropTypes.func,
  onAddCheckpointAtTime: PropTypes.func,
  onUpdateCheckpointTime: PropTypes.func,
  transcriptUrl: PropTypes.string,
};

const getDerivedProps = props => {
  const { entitySubType } = props;
  const isAITutorChat = entitySubType === "ai_tutor_chat";

  return { isAITutorChat };
};

const mapActionCreators = {
  setToastMsg,
};

CheckpointMarker.displayName = "CheckpointMarker";

export default compose(
  connect(null, mapActionCreators),
  withDerivedProps(getDerivedProps)
)(AITutorVideoModality);
